import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { Loading } from 'components/Loading'

import { useAuthContext } from 'hooks/useUserContext'
import { useViewAs } from 'hooks/useViewAs'

import { initPostHog, isPostHogEnabled, posthog } from 'lib/posthog'

export function PostHogSetup({ children }: { children: ReactNode }) {
  const { authLoaded } = useAuthContext()
  const viewAs = useViewAs()
  const [decided, setDecided] = useState(false)

  // PostHog starts only after the session is known, so a view-as session is
  // never recorded or attributed to the target. Never opt_out_capturing here:
  // it persists in the viewer's browser. Children mount only once this
  // decision is made (posthog-js drops calls made before init), and the tree
  // shape never changes afterwards, so they mount exactly once.
  useEffect(() => {
    if (!authLoaded || decided) return
    if (!viewAs) initPostHog()
    setDecided(true)
  }, [authLoaded, viewAs, decided])

  const active = decided && !viewAs && isPostHogEnabled()

  return (
    <PHProvider client={posthog}>
      {active ? <PostHogIdentifier /> : null}
      {decided ? children : <Loading />}
    </PHProvider>
  )
}

function PostHogIdentifier() {
  const {
    currentUser,
    permissions,
    hasGlobalViewPerm,
    hasGlobalEditPerm,
    hasGlobalAdminPerm,
    global,
  } = useAuthContext()

  useEffect(() => {
    if (currentUser) {
      const orgs = permissions?.organizations?.filter((o) => o.role !== null)
      const projects = permissions?.projects?.filter((p) => p.role !== null)
      const globalRoles =
        permissions?.global?.map((g) => g.role).filter(Boolean) ?? []

      try {
        // Person properties -- available for segmentation in PostHog dashboards
        posthog.identify(String(currentUser.id), {
          email: currentUser.email,
          has_global_view: hasGlobalViewPerm,
          has_global_edit: hasGlobalEditPerm,
          has_global_admin: hasGlobalAdminPerm,
          is_cable_admin: global.cableAdmin,
          is_logger_admin: global.loggerAdmin,
          global_roles: globalRoles,
          organization_ids: orgs?.map((o) => o.id) ?? [],
          organization_names: orgs?.map((o) => o.organizationName) ?? [],
          organization_count: orgs?.length ?? 0,
          project_ids: projects?.map((p) => p.id) ?? [],
          project_count: projects?.length ?? 0,
        })

        // Super properties -- auto-attached to every event
        posthog.register({
          source: 'web',
          user_email: currentUser.email,
          organization_ids: orgs?.map((o) => o.id) ?? [],
          organization_names: orgs?.map((o) => o.organizationName) ?? [],
          organization_count: orgs?.length ?? 0,
          project_count: projects?.length ?? 0,
          role_level: hasGlobalAdminPerm
            ? 'global_admin'
            : hasGlobalEditPerm
              ? 'global_edit'
              : hasGlobalViewPerm
                ? 'global_view'
                : 'org_or_project',
        })

        // Register all orgs as groups. Each call fires a $groupidentify event
        // (needed for group analytics), but only the last call sets the active
        // group context for subsequent events. This is a PostHog limitation --
        // multi-org users will have their events attributed to their last org.
        // The org arrays in person/super properties provide the full picture.
        if (orgs?.length) {
          for (const org of orgs) {
            posthog.group('organization', String(org.id), {
              name: org.organizationName,
            })
          }
        }
      } catch {
        // PostHog SDK call failed -- analytics degraded but app continues
      }
    } else {
      try {
        posthog.reset()
      } catch {
        // PostHog SDK call failed -- analytics degraded but app continues
      }
    }
  }, [
    currentUser,
    permissions,
    hasGlobalViewPerm,
    hasGlobalEditPerm,
    hasGlobalAdminPerm,
    global,
  ])

  return null
}
